import { Link } from 'react-router-dom';
import { UnstyledButton } from '@mantine/core';
import type { Correction } from '@nelnela/contracts';

/**
 * The correction card (design `8d`), shared by the journal and by chat.
 *
 * It lived in `screens/Journal.tsx` for one commit, and `screens/Chat.tsx` imported it from there
 * — a screen importing a screen, which `Journal.tsx`'s own comment about declining to reach into
 * `Read.tsx` had already argued against. One card serving two surfaces is the milestone's stated
 * design (M6c-ii "inherits and therefore must not rebuild" names it first), so it belongs here.
 *
 * Nothing about it is journal-shaped: it takes a `Correction` off the wire, which is the same type
 * on a composition version and on a conversation turn, and three callbacks. The caller decides what
 * Accept means to it — splicing text in a textarea, or rewriting a sent bubble.
 */

export interface CorrectionCardProps {
  correction: Correction;
  onAccept: () => void;
  onEdit: () => void;
  onIgnore: () => void;
}

/**
 * `8d`'s four rows: what · why · Accept / Edit / Ignore · `lesson →`.
 *
 * Docked above the suggestion strip rather than floating by the word. The design made that call
 * and it is the one that makes the caret-driven open possible; it also means the card never
 * covers the sentence it is about, which a popover over a two-line paragraph on a 360 px phone
 * inevitably does.
 */
export function CorrectionCard({ correction, onAccept, onEdit, onIgnore }: CorrectionCardProps) {
  return (
    <div
      style={{
        margin: '0 14px 10px',
        background: 'var(--nn-panel)',
        border: '1px solid var(--nn-hairline)',
        borderRadius: 10,
        padding: '11px 13px',
        boxShadow: '0 10px 30px rgba(0,0,0,.35)',
        flexShrink: 0,
      }}
      role="group"
      aria-label="Correction"
    >
      <div lang="ka" style={{ fontSize: 13.5, color: 'var(--nn-text)' }}>
        {correction.original} →{' '}
        <b style={{ color: 'var(--nn-text-strong)' }}>{correction.suggested}</b>
      </div>
      {/* The model's one line if it wrote one, else the vocabulary's label for the tag. Never
          nothing: a mark with no reason is an accusation. */}
      <div style={{ marginTop: 3, fontSize: 11.5, color: 'var(--nn-muted)' }}>
        {correction.explanation || correction.errorLabel}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 9 }}>
        <UnstyledButton
          onClick={onAccept}
          style={{ fontSize: 12, color: 'var(--nn-accent-text)', fontWeight: 600 }}
        >
          Accept
        </UnstyledButton>
        <UnstyledButton onClick={onEdit} style={{ fontSize: 12, color: 'var(--nn-text)' }}>
          Edit
        </UnstyledButton>
        <UnstyledButton onClick={onIgnore} style={{ fontSize: 12, color: 'var(--nn-muted)' }}>
          Ignore
        </UnstyledButton>
        <span style={{ flex: 1 }} />
        {/* Only when the corrected word actually has a lesson. The server resolves it from
            `lesson.item_id`, so a lesson written later lights this row up with no code change. */}
        {correction.lessonId && (
          <UnstyledButton
            component={Link}
            to={`/words/lesson/${correction.lessonId}`}
            style={{ fontSize: 11, color: 'var(--nn-link)' }}
          >
            lesson →
          </UnstyledButton>
        )}
      </div>
    </div>
  );
}
